import { useCallback, useState } from 'react';
import { useDebounce } from 'react-use';
import { Add } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';

import { CloseCircleIcon, SearchIcon } from '~assets/icons';
import { PlacementListCard } from '~components/molecules/PlacementListCard';
import { PINNING_STEP, usePinningStepContext } from '~providers';
import { IFormCreationValues, INITIAL_VALUES } from '~providers/useForm';
import { StationPlacementType } from '~types';

const SEARCH_DEBOUNCE_TIME = 500;

type Props = {
  placements: StationPlacementType[];
  onSearchPlacement: (search: string) => void;
  onSelectPlacementCallback?: (placement: StationPlacementType | null) => void;
  currentPlacementListInPinningForm?: string[];
  isLoadingPlacements: boolean;
};

export const SelectExistingPlacementStep: React.FC<Props> = ({
  placements,
  onSearchPlacement,
  onSelectPlacementCallback,
  currentPlacementListInPinningForm,
  isLoadingPlacements,
}) => {
  const {
    selectedPlacementId,
    handleSetSelectedPlacementId,
    handleSetPinningStep,
    setIsPlacementAssignedStation,
    setIsRelocationFlow,
    setIsAssigningToExistingPlacement,
    setAssignedStationId,
    setAssignedStationImei,
    setAssignedStationType,
  } = usePinningStepContext();

  const [searchInput, setSearchInput] = useState('');

  useDebounce(
    () => {
      onSearchPlacement(searchInput);
    },
    SEARCH_DEBOUNCE_TIME,
    [searchInput],
  );

  const { setValues } = useFormikContext<IFormCreationValues>();

  const handleClearSearch = () => setSearchInput('');

  /**
   * Picking a placement starts a fresh movement, so the reason and note are cleared
   * at both levels and the operator fills them in again. Stepping back from the
   * review screen does not come through here, so reviewed values survive.
   */
  const resetMovementInput = useCallback(() => {
    setValues((current) => ({
      ...current,
      swapReasonId: INITIAL_VALUES.swapReasonId,
      swapNote: INITIAL_VALUES.swapNote,
      relocationReasonId: INITIAL_VALUES.relocationReasonId,
      relocationNote: INITIAL_VALUES.relocationNote,
      placement: {
        ...current.placement,
        swapReasonId: INITIAL_VALUES.placement.swapReasonId,
        swapNote: INITIAL_VALUES.placement.swapNote,
        relocationReasonId: INITIAL_VALUES.placement.relocationReasonId,
        relocationNote: INITIAL_VALUES.placement.relocationNote,
      },
    }));
  }, [setValues]);

  const handleGoToNextStep = useCallback(
    (placement: StationPlacementType) => {
      resetMovementInput();
      handleSetSelectedPlacementId(placement.id);
      onSelectPlacementCallback && onSelectPlacementCallback(placement);
      if (placement.type == undefined) {
        setIsPlacementAssignedStation(false);
        setIsRelocationFlow(false);
        setIsAssigningToExistingPlacement(true);
        handleSetPinningStep(PINNING_STEP.ASSIGN_STATION_SCAN_QR);
      } else {
        setIsPlacementAssignedStation(true);
        setIsRelocationFlow(false);
        setIsAssigningToExistingPlacement(false);
        setAssignedStationId(placement.stationId);
        setAssignedStationImei(placement.imei);
        setAssignedStationType(placement.type);
        handleSetPinningStep(PINNING_STEP.ASSIGN_STATION_SELECT_ACTION);
      }
    },
    [
      handleSetPinningStep,
      handleSetSelectedPlacementId,
      onSelectPlacementCallback,
      setIsAssigningToExistingPlacement,
      setIsPlacementAssignedStation,
      setIsRelocationFlow,
      setAssignedStationId,
      setAssignedStationImei,
      setAssignedStationType,
      resetMovementInput,
    ],
  );

  const handleClickCreateNewPlacement = useCallback(() => {
    resetMovementInput();
    handleSetSelectedPlacementId(undefined);
    onSelectPlacementCallback && onSelectPlacementCallback(null);
    setIsPlacementAssignedStation(false);
    setIsRelocationFlow(false);
    setIsAssigningToExistingPlacement(false);
    handleSetPinningStep(PINNING_STEP.ASSIGN_STATION_SCAN_QR);
  }, [
    handleSetPinningStep,
    handleSetSelectedPlacementId,
    onSelectPlacementCallback,
    setIsAssigningToExistingPlacement,
    setIsPlacementAssignedStation,
    setIsRelocationFlow,
    resetMovementInput,
  ]);
  return (
    <Stack gap={2} pb={4}>
      {isLoadingPlacements ? (
        <Skeleton height={200} />
      ) : (
        <>
          <Stack
            gap={2}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              pt: 1,
              pb: 1,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Select a Placement
            </Typography>
            <TextField
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search Placement Name"
              InputProps={{
                startAdornment: (
                  <Stack marginLeft={1} marginRight={0.5}>
                    <SearchIcon />
                  </Stack>
                ),
                endAdornment: searchInput ? (
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                    sx={{ marginRight: '0.5rem' }}
                  >
                    <CloseCircleIcon />
                  </IconButton>
                ) : null,
                sx: {
                  borderRadius: '100px',
                  padding: '5px 0 !important',
                },
              }}
              inputProps={{
                sx: { padding: '7px 4px' },
                autoComplete: 'off',
              }}
            />
            {placements.length !== 0 && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">
                  {placements.length} Available
                </Typography>
                <Button
                  startIcon={<Add />}
                  variant="outlined"
                  sx={{
                    borderRadius: 100,
                    paddingTop: '6px',
                    paddingBottom: '6px',
                  }}
                  onClick={() => handleClickCreateNewPlacement()}
                >
                  Create
                </Button>
              </Stack>
            )}
          </Stack>

          {placements.length !== 0 ? (
            <Stack gap={1}>
              {placements.map((e) => (
                <PlacementListCard
                  key={e.id}
                  placement={e}
                  onClick={handleGoToNextStep}
                  selectedPlacementId={selectedPlacementId}
                  disabled={currentPlacementListInPinningForm?.includes(e.id)}
                />
              ))}
            </Stack>
          ) : (
            <Stack width="100%" gap={4}>
              <Stack alignItems="center" pt={2}>
                <Typography
                  variant="body1"
                  textAlign="center"
                  width="fit-content"
                >
                  No results found
                </Typography>
                <Typography
                  color="text.placeholder"
                  variant="body2"
                  maxWidth={200}
                  textAlign="center"
                >
                  Try a different search term or create a new Placement.
                </Typography>
              </Stack>

              <Button
                fullWidth
                size="large"
                variant="contained"
                style={{ borderRadius: '100px' }}
                onClick={() => handleClickCreateNewPlacement()}
                startIcon={<Add />}
              >
                Create Placement
              </Button>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};
